/**
 * Clerk error → one plain sentence (custom sign-in / sign-up, 3 Oct 2026).
 *
 * Clerk's Frontend API rejects with a `ClerkAPIResponseError` whose
 * `errors[]` each carry a machine `code` and Clerk's own English. Our
 * pages never show that object, or Clerk's wording: every code we
 * expect on these flows maps to a sentence in Tarii's voice, plus an
 * optional follow-up the page can offer as a link (create an account,
 * reset the password, …). Anything unmapped gets a calm generic line.
 *
 * Codes are taken from Clerk's custom-flow docs and the `unstable__errors`
 * keys in `@clerk/localizations` (en-US). The instance runs with
 * enumeration protection OFF, so an unknown email comes back as
 * `form_identifier_not_found` and we can say so; if that setting is ever
 * switched on the combined `form_password_or_identifier_incorrect` code
 * is covered too.
 */
import { isClerkAPIResponseError } from "@clerk/clerk-react/errors";

/** A follow-up the page can render next to the sentence. */
export type AuthErrorAction =
  | "sign-up" // no account for this email → create one
  | "sign-in" // the email already has an account → sign in
  | "reset-password" // the password must be replaced
  | "resend-code" // the code is stale → send a new one
  | "signed-in" // a session already exists on this browser
  | "sign-out-first" // #3005: single-session Clerk → sign out, then sign in as the other account
  | "choose-account"; // #3005: that account is already signed in here → pick it

export interface FriendlyAuthError {
  message: string;
  /** Clerk's machine code, kept for tests and logs (never rendered). */
  code: string | null;
  action?: AuthErrorAction;
}

/** Which form raised the error — a few codes read differently per flow.
 *  `add` is **Add another account** (#3005): a sign-in while signed in. */
export type AuthErrorFlow = "sign-in" | "sign-up" | "code" | "reset" | "add";

/** #3005 — the add page's sentences for the two already-signed-in codes. */
export const ADD_ACCOUNT_ERRORS = {
  oneAtATime:
    "This browser keeps one account signed in at a time. Sign out first, then sign in as the other account.",
  alreadyHere: "That account is already signed in on this browser. Pick it from Switch accounts.",
} as const;

const GENERIC = "Something went wrong on our side. Try again in a moment.";

interface ApiErrorLike {
  code: string;
  meta?: { paramName?: string };
}

/**
 * The first API error, or null. `isClerkAPIResponseError` recognises the
 * class by its static `kind`, which clerk-js (loaded from Clerk's CDN, a
 * different bundle from this one) sets too; the duck-typed fallback keeps
 * us honest if a clerk-js release ever ships without it. The guard throws
 * on a falsy argument, hence the early return.
 */
function first(err: unknown): { code: string; paramName: string | undefined } | null {
  if (!err || typeof err !== "object") return null;
  let errors: ReadonlyArray<ApiErrorLike> | null = null;
  if (isClerkAPIResponseError(err)) {
    errors = err.errors;
  } else if (Array.isArray((err as { errors?: unknown }).errors)) {
    errors = (err as { errors: ApiErrorLike[] }).errors;
  }
  const e = errors?.[0];
  if (!e || typeof e.code !== "string") return null;
  return { code: e.code, paramName: e.meta?.paramName };
}

/** clerk-js wraps a failed fetch as a runtime error with this code. */
function isNetworkError(err: unknown): boolean {
  return (
    !!err &&
    typeof err === "object" &&
    "code" in err &&
    (err as { code?: unknown }).code === "network_error"
  );
}

export function describeClerkError(err: unknown, flow: AuthErrorFlow): FriendlyAuthError {
  if (isNetworkError(err)) {
    return {
      message: "We couldn't reach the sign-in service. Check your connection and try again.",
      code: "network_error",
    };
  }
  const e = first(err);
  if (!e) return { message: GENERIC, code: null };
  const { code, paramName } = e;

  switch (code) {
    // ── who you are ────────────────────────────────────────────────
    case "form_identifier_not_found":
    case "invitation_account_not_exists":
      return {
        message: "There's no Tarii account for that email. Check the spelling, or create one.",
        code,
        action: "sign-up",
      };
    case "form_password_or_identifier_incorrect":
      return {
        message: "That email and password don't match. Try again, or email yourself a code.",
        code,
      };
    case "form_identifier_exists":
    case "form_identifier_exists__email_address":
      return {
        message: "There's already an account with that email. Sign in instead.",
        code,
        action: "sign-in",
      };
    case "form_param_format_invalid":
    case "form_param_format_invalid__email_address":
    case "form_param_type_invalid__email_address":
      return {
        message:
          paramName && !paramName.includes("email")
            ? "One of the details isn't in the right format. Check it and try again."
            : "That doesn't look like an email address. Check it and try again.",
        code,
      };
    case "form_email_address_blocked":
    case "not_allowed_access":
    case "sign_up_restricted_waitlist":
      return {
        message:
          "This email can't be used to create an account here. Ask your team lead for an invite, or use another address.",
        code,
      };
    case "form_param_nil":
    case "form_param_missing":
      return {
        message:
          paramName === "password"
            ? "Enter your password."
            : paramName === "code"
              ? "Enter the code from the email."
              : "Fill in your email address first.",
        code,
      };

    // ── passwords ──────────────────────────────────────────────────
    case "form_password_incorrect":
      return {
        message: "That password isn't right. Try again, or email yourself a code instead.",
        code,
      };
    case "strategy_for_user_invalid":
      return {
        message:
          flow === "sign-in"
            ? "This account doesn't use a password. Continue with Google, or email yourself a code."
            : "That way of signing in isn't set up for this account.",
        code,
      };
    case "form_password_pwned":
    case "form_password_pwned__sign_in":
    case "form_password_compromised__sign_in":
    case "form_password_compromised":
    case "form_password_untrusted__sign_in":
      return flow === "sign-in"
        ? {
            message:
              "This password has turned up in a known data leak, so it can't be used any more. Reset it to carry on.",
            code,
            action: "reset-password",
          }
        : {
            message: "That password has turned up in a known data leak. Choose a different one.",
            code,
          };
    case "form_password_length_too_short":
      return { message: "Use a password of at least 8 characters.", code };
    case "form_password_size_in_bytes_exceeded":
      return { message: "That password is too long. Choose a shorter one.", code };
    case "form_password_not_strong_enough":
    case "form_password_validation_failed":
      return {
        message: "That password is too easy to guess. Try a longer one, or add a few words.",
        code,
      };
    case "form_password_matches_identifier":
      return { message: "Your password can't be the same as your email.", code };
    case "form_new_password_matches_current":
      return { message: "Choose a password you haven't used here before.", code };

    // ── codes ──────────────────────────────────────────────────────
    case "form_code_incorrect":
      return {
        message: "That code isn't right. Check the latest email and try again.",
        code,
      };
    case "verification_expired":
      return {
        message: "That code has expired. Send a new one.",
        code,
        action: "resend-code",
      };
    case "verification_failed":
      return {
        message: "Too many wrong codes. Send a new one and try again.",
        code,
        action: "resend-code",
      };

    // ── too many attempts ──────────────────────────────────────────
    case "user_locked":
      return {
        message:
          "Too many attempts, so sign-in for this account is paused for a while. Try again later.",
        code,
      };
    case "too_many_requests":
    case "rate_limit_exceeded":
      return {
        message: "Too many attempts in a short time. Wait a minute, then try again.",
        code,
      };

    // ── session and attempt state ──────────────────────────────────
    // #3005 — on Add another account: `session_exists` means Clerk runs in
    // single-session mode (multi-session is not on for the instance);
    // `identifier_already_signed_in` means that account is in the list.
    case "session_exists":
      return flow === "add"
        ? { message: ADD_ACCOUNT_ERRORS.oneAtATime, code, action: "sign-out-first" }
        : { message: "You're already signed in on this browser.", code, action: "signed-in" };
    case "identifier_already_signed_in":
      return flow === "add"
        ? { message: ADD_ACCOUNT_ERRORS.alreadyHere, code, action: "choose-account" }
        : { message: "You're already signed in on this browser.", code, action: "signed-in" };
    case "client_state_invalid":
    case "sign_in_expired":
    case "sign_up_expired":
    case "resource_not_found":
      return { message: "This attempt timed out. Start again from the top.", code };

    // ── bot protection (sign-up) ───────────────────────────────────
    case "captcha_invalid":
    case "captcha_unavailable":
    case "captcha_missing_token":
      return {
        message:
          "We couldn't finish a quick security check. Refresh the page and try again; if it keeps happening, try another browser.",
        code,
      };

    // ── Google ─────────────────────────────────────────────────────
    case "oauth_access_denied":
      return { message: "Google sign-in was cancelled. Try again when you're ready.", code };
    case "external_account_exists":
      return {
        message: "That Google account is already linked to a Tarii account. Sign in instead.",
        code,
        action: "sign-in",
      };
    case "external_account_not_found":
      return {
        message: "There's no Tarii account for that Google account yet. Create one first.",
        code,
        action: "sign-up",
      };

    default:
      return { message: GENERIC, code };
  }
}
